<template>
    <tailwind-layout title="Property Added">
        <div class="flex min-h-[60vh] flex-col items-center justify-center px-2 py-10 text-center">
            <h1 class="text-4xl font-extrabold text-rent-risk-blue-400 sm:text-6xl lg:text-7xl">You did it, congrats!</h1>

            <p class="mt-6 max-w-2xl text-base text-gray-700">
                You've successfully added a property. Now, you can share your screening link with applicants
                <span aria-hidden="true">↓</span>
            </p>

            <a
                :href="property.application_link"
                target="_blank"
                rel="noopener"
                class="mt-3 max-w-full break-all text-base text-blue-700 underline hover:text-blue-900"
                data-test="screening-link"
            >
                {{ property.application_link }}
            </a>

            <button
                type="button"
                class="mt-6 inline-flex w-full max-w-sm items-center justify-center gap-2 rounded-lg bg-gold-800 px-6 py-3 text-lg font-bold text-rent-risk-blue-400 transition-colors hover:bg-gold-900 focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2"
                data-test="copy-screening-link"
                @click="copyLink"
            >
                <font-awesome-icon :icon="copyState === 'copied' ? faCheck : faCopy" aria-hidden="true" />
                {{ copyLabel }}
            </button>
            <p class="sr-only" aria-live="polite">{{ copyState === 'copied' ? 'Link copied to clipboard' : '' }}</p>

            <Link
                :href="route('properties.show', property.id)"
                class="mt-4 text-sm text-gray-600 underline hover:text-gray-900"
                data-test="explore-features"
            >
                Explore other features.
            </Link>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck, faCopy } from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { rainConfetti } from '@/Shared/confetti'
import { recordOnboardingTask, SHARE_LINK_TASK } from '@/Shared/onboardingTasks'

/**
 * 86bc2cgxd: shown after every property the landlord adds, before its details page.
 */
const props = defineProps({
    property: {
        type: Object,
        required: true,
    },
})

const page = usePage()

// idle | copied | failed
const copyState = ref('idle')

const copyLabel = computed(() => ({
    copied: 'Copied!',
    failed: 'Copy failed — select the link above',
}[copyState.value] ?? 'Copy My Screening Link'))

let resetTimer = null

const copyLink = async () => {
    try {
        await navigator.clipboard.writeText(props.property.application_link)
        copyState.value = 'copied'
    } catch {
        copyState.value = 'failed'
    }

    clearTimeout(resetTimer)
    resetTimer = setTimeout(() => {
        copyState.value = 'idle'
    }, 3000)

    // Ticks even when the copy failed, like the share modal: the landlord meant to share.
    if (page.props.onboarding?.active) {
        recordOnboardingTask(SHARE_LINK_TASK)
    }
}

let stopConfetti = () => {}

onMounted(() => {
    stopConfetti = rainConfetti()
})

onBeforeUnmount(() => {
    stopConfetti()
})
</script>
